import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { PageContainerService, TimerService } from '../../services';
import { ProjectAccessArea, PROJECT_IDLE_TIMEOUT_MS } from '../../utilities/project-access-area';
import {
  faBuilding,
  faBoxOpen,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faPenAlt,
  faStar,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  ReadinessSummaryDto,
  ModuleDisplaySettingDto,
  GetReadinessSummaryCommandRequest,
  GetReadinessSummaryCommandResponse,
  MaterialsStageStatusDto,
  GetMaterialsStatusCommandRequest,
  GetMaterialsStatusCommandResponse,
  ExecutionSummaryDto,
  GetExecutionStatusCommandRequest,
  GetExecutionStatusCommandResponse,
  ExecutionChartDataDto,
  GetExecutionChartCommandRequest,
  GetExecutionChartCommandResponse,
  GetExecutionCurrentStatusChartCommandRequest,
  GetExecutionCurrentStatusChartCommandResponse,
  ExecutionBarChartDto,
  GetProjectModuleDisplaySettingsCommandRequest,
  GetProjectModuleDisplaySettingsCommandResponse,
  GetProjectSetupCommandRequest,
  GetProjectSetupCommandResponse,
  GetExecutionBarChartCommandRequest,
  GetExecutionBarChartCommandResponse,
  ExecutionChartDataSeriesDto,
  ProjectSetupDto,
  UserRoleDto,
  GetP6ReportingLinkCommandRequest,
  GetP6ReportingLinkCommandResponse,
  GetProjectAiInsightsCommandRequest,
  GetProjectAiInsightsCommandResponse
} from '../../data/CommandServiceDtos.generated';
import { formatStamp } from './summary-ui';
import { faMagic, faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  standalone: false,
  selector: 'project-summary',
  templateUrl: './project-summary.component.html',
  styleUrls: ['./project-summary.component.scss']
})
export class ProjectSummaryComponent extends ControllerForViewBase implements OnInit, OnDestroy {
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faPencil = faPenAlt;
  faStar = faStar;
  faBuilding = faBuilding;
  faBoxOpen = faBoxOpen;
  faSyncAlt = faSyncAlt;
  faMagic = faMagic;
  faSpinner = faSpinner;

  project: ProjectSetupDto = new ProjectSetupDto();
  projectId: number;
  powerBiReportPath: string;
  projectDisplayType: string;
  readinessSummary: ReadinessSummaryDto;
  materialsSummary: MaterialsStageStatusDto;
  executionSummary: ExecutionSummaryDto;
  executionChartData: ExecutionChartDataDto[];
  executionBarChartData: ExecutionChartDataSeriesDto[];
  executionLineChartData: ExecutionBarChartDto;
  moduleDisplaySetting: ModuleDisplaySettingDto;
  cxPlannerSummary = false;
  cxPlannerSummaryStage: any;
  userRoles: UserRoleDto[];
  showEditIconForUser = false;
  isAutoRouted = false;
  isFavorited = false;
  editMenuOpen = false;
  openingDetailedReporting = false;
  dataAsOf: Date = new Date();
  refreshing = false;
  currentSlideIndex = 0;
  insightsText = '';
  insightsLoading = false;
  insightsGeneratedAt: Date = null;
  /** Project that the current insightsText belongs to; null when cleared. */
  insightsProjectId: number = null;
  private routeSub: Subscription;

  constructor(
    public pcs: PageContainerService,
    private route: ActivatedRoute,
    private router: Router,
    public timerService: TimerService,
    private cdr: ChangeDetectorRef
  ) {
    super(pcs);
  }

  ngOnInit() {
    // Clear/hide insights whenever the :id route param changes (e.g. Latest Accessed).
    this.routeSub = this.route.paramMap
      .pipe(
        map(params => +(params.get('id') || 0)),
        filter(id => !!id && !isNaN(id))
      )
      .subscribe(id => this.onUrlProjectIdChanged(id));

    this.routeSub.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          const id = +(this.route.snapshot.paramMap.get('id') || 0);
          if (id) {
            this.onUrlProjectIdChanged(id);
          }
        })
    );
  }

  /**
   * Insights are visible only when they were generated for the same project
   * currently shown in the URL / header (guards against stale text after navigation).
   */
  get hasVisibleInsights(): boolean {
    if (!this.insightsText || this.insightsProjectId == null) {
      return false;
    }
    const loadedProjectId = this.project?.ProPakProject?.ProjectId;
    if (loadedProjectId != null && this.insightsProjectId !== loadedProjectId) {
      return false;
    }
    return this.insightsProjectId === this.projectId;
  }

  private onUrlProjectIdChanged(nextProjectId: number): void {
    if (!nextProjectId || isNaN(nextProjectId)) {
      return;
    }

    // Drop stale insights even if projectId was already updated somehow.
    if (this.insightsText && this.insightsProjectId !== nextProjectId) {
      this.clearAiInsights();
      this.cdr.detectChanges();
    }

    if (this.projectId === nextProjectId) {
      return;
    }

    this.clearAiInsights();
    this.projectId = nextProjectId;
    this.cdr.detectChanges();
    void this.loadPage(this.projectId);
  }

  get dataAsOfLabel(): string {
    return formatStamp(this.dataAsOf, false);
  }

  get showReadinessColumn(): boolean {
    return !!(this.moduleDisplaySetting?.DisplayReadiness
      || this.readinessSummary?.Project?.IsReadinessFormEnabled
      || this.readinessSummary?.ReadinessFileLocation);
  }

  get showMaterialsColumn(): boolean {
    return !!this.moduleDisplaySetting?.DisplayMaterials;
  }

  get showExecutionColumn(): boolean {
    return !!(this.moduleDisplaySetting?.DisplayExecution && this.project?.Id > 0);
  }

  get showCommissioningColumn(): boolean {
    return !!(this.project?.CXPlannerIdentifiers?.length > 0);
  }

  get totalSlides(): number {
    let count = 0;
    if (this.showReadinessColumn) {
      count++;
    }
    if (this.showMaterialsColumn) {
      count++;
    }
    if (this.showExecutionColumn) {
      count++;
    }
    if (this.showCommissioningColumn) {
      count++;
    }
    return count;
  }

  isColumnVisible(column: 'readiness' | 'materials' | 'execution' | 'commissioning'): boolean {
    const columns: Array<'readiness' | 'materials' | 'execution' | 'commissioning'> = [];
    if (this.showReadinessColumn) {
      columns.push('readiness');
    }
    if (this.showMaterialsColumn) {
      columns.push('materials');
    }
    if (this.showExecutionColumn) {
      columns.push('execution');
    }
    if (this.showCommissioningColumn) {
      columns.push('commissioning');
    }
    const index = columns.indexOf(column);
    return index >= this.currentSlideIndex && index < this.currentSlideIndex + 3;
  }

  get canMoveLeft(): boolean {
    return this.totalSlides > 3 && this.currentSlideIndex > 0;
  }

  get canMoveRight(): boolean {
    return this.totalSlides > 3 && this.currentSlideIndex < this.totalSlides - 3;
  }

  async loadPage(id: number) {
    // Always clear when loading a project that does not own the current insights.
    if (this.insightsText && this.insightsProjectId !== id) {
      this.clearAiInsights();
      this.cdr.detectChanges();
    }
    await this.getModuleDisplaySettings(id);
    this.loadReadinessData(id);
    if (this.moduleDisplaySetting.DisplayMaterials) {
      this.loadMaterialsData(id);
    }
    if (this.moduleDisplaySetting.DisplayExecution) {
      this.loadExecutionData(id);
      this.loadExecutionStatusChartData(id);
    }
    this.loadProjectData(id);
  }

  async refreshData() {
    if (!this.projectId || this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.clearAiInsights();
    this.cdr.detectChanges();
    this.readinessSummary = null;
    this.materialsSummary = null;
    this.executionSummary = null;
    this.executionLineChartData = null;
    this.cxPlannerSummary = false;
    try {
      await this.loadPage(this.projectId);
      this.dataAsOf = new Date();
    } finally {
      this.refreshing = false;
    }
  }

  async getModuleDisplaySettings(id: number) {
    const payload: GetProjectModuleDisplaySettingsCommandRequest = { ProPakProjectId: id };
    const response: GetProjectModuleDisplaySettingsCommandResponse =
      await this.pcs.apiService.GetProjectModuleDisplaySettings(payload, true, false);
    this.moduleDisplaySetting = response.ModuleDisplaySettings;
  }

  async loadReadinessData(id: number) {
    const payload: GetReadinessSummaryCommandRequest = { ProjectId: id, ProPakProjectId: id };
    const response: GetReadinessSummaryCommandResponse =
      await this.pcs.apiService.GetReadinessSummary(payload, false, false);

    if (response.ReadinessSummary.Priorities.length > 0) {
      this.readinessSummary = response.ReadinessSummary;
    }
  }

  async loadMaterialsData(id: number) {
    const payload: GetMaterialsStatusCommandRequest = { ProjectId: id };
    const response: GetMaterialsStatusCommandResponse =
      await this.pcs.apiService.GetMaterialsStatus(payload, false, false);
    if (response.Materials.MaterialsAccessUrl != null && response.Materials.MaterialsSchemas.length != 0) {
      this.materialsSummary = response.Materials;
    }
  }

  async loadExecutionData(id: number) {
    const payload: GetExecutionStatusCommandRequest = { ProjectId: id };
    const response: GetExecutionStatusCommandResponse =
      await this.pcs.apiService.GetExecutionStatus(payload, false, false);
    this.executionSummary = response.Execution;
  }

  async loadExecutionChartData(id: number) {
    const payload: GetExecutionChartCommandRequest = { ProjectId: id, StartDate: null, EndDate: null };
    const response: GetExecutionChartCommandResponse =
      await this.pcs.apiService.GetExecutionChart(payload, false, false);
    this.executionChartData = response.Data.ChartData;
  }

  async loadExecutionBarChartData(id: number) {
    const payload: GetExecutionBarChartCommandRequest = { ProjectId: id };
    const response: GetExecutionBarChartCommandResponse =
      await this.pcs.apiService.GetExecutionBarChart(payload, false, false);
    this.executionBarChartData = response.Data;
  }

  async loadExecutionStatusChartData(id: number) {
    const payloadStatusChart: GetExecutionCurrentStatusChartCommandRequest = { ProjectId: id };
    const responseStatusChart: GetExecutionCurrentStatusChartCommandResponse =
      await this.pcs.apiService.GetExecutionCurrentStatusChart(payloadStatusChart, true, false);
    this.executionLineChartData = responseStatusChart.Data;
  }

  private parseCxPlannerPayload(data: any): any[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (typeof data._body === 'string') {
      try {
        return this.parseCxPlannerPayload(JSON.parse(data._body));
      } catch {
        return [];
      }
    }
    if (Array.isArray(data.data)) {
      return data.data;
    }
    if (Array.isArray(data.checklists)) {
      return data.checklists;
    }
    return [];
  }

  private transformData(dataArray: any[]): any[] {
    if (!Array.isArray(dataArray)) {
      return [];
    }
    return dataArray.map((data) => {
      return {
        SummaryStageId: data.id,
        StageName: data.name,
        PercentComplete: data.sub_per,
        Stages: data.children ? data.children.map((child: any): any => ({
          StageId: child.id,
          StageName: child.name,
          PercentComplete: child.sub_per
        })) : []
      };
    });
  }

  storeRecentProject(projectData: any): void {
    const storageKey = 'recentProjects';
    let recentProjects = JSON.parse(localStorage.getItem(storageKey) || '[]');

    const projectId = projectData.ProPakProject.ProjectId || projectData.ProPakProject.id;
    recentProjects = recentProjects.filter((p: any) =>
      (p.ProjectId || p.id) !== projectId
    );

    const favoriteIds = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
    const project = projectData;
    const projectWithFlags = {
      ...project.ProPakProject,
      lastAccessed: new Date().toISOString(),
      ReadinessAssessmentPath: project.ReadinessAssessmentPath,
      DisplayReadiness: project.ReadinessAssessmentPath != null || project.DisplayReadiness,
      SiteSenseIdentifiers: project.SiteSenseIdentifiers,
      HasMaterials: project.SiteSenseIdentifiers != null && project.SiteSenseIdentifiers.length > 0,
      PrimaveraIdentifiers: project.PrimaveraIdentifiers,
      HasExecution: project.PrimaveraIdentifiers != null && project.PrimaveraIdentifiers.length > 0,
      CXPlannerIdentifiers: project.CXPlannerIdentifiers,
      HasCXPlanner: project.CXPlannerIdentifiers != null && project.CXPlannerIdentifiers.length > 0,
      IsFavorite: favoriteIds.includes(projectId)
    };
    recentProjects.unshift(projectWithFlags);
    if (recentProjects.length > 5) {
      recentProjects = recentProjects.slice(0, 5);
    }
    localStorage.setItem(storageKey, JSON.stringify(recentProjects));
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('recentProjectsUpdated', { detail: { projectId: projectId } }));
    }
  }

  async loadProjectData(projectId) {
    const request: GetProjectSetupCommandRequest = { ProjectId: projectId, logTimeIn: true, AccessArea: ProjectAccessArea.ProjectSummary };
    const response: GetProjectSetupCommandResponse = await this.pcs.apiService.GetProjectSetup(request, true, false);
    this.timerService.startTimer(projectId);
    this.timerService.startIdleMonitor(PROJECT_IDLE_TIMEOUT_MS, () => {
      this.isAutoRouted = true;
      this.router.navigate(['/dashboard']);
    });
    this.project = response.ProjectInfo;
    const loadedId = this.project?.ProPakProject?.ProjectId;
    if (this.insightsText && loadedId != null && this.insightsProjectId !== loadedId) {
      this.clearAiInsights();
      this.cdr.detectChanges();
    }
    this.loadFavoriteStatus();
    this.storeRecentProject(this.project);
    this.powerBiReportPath = response.ProjectInfo.PowerBiReportPath;
    this.projectDisplayType = response.ProjectInfo.ProjectDisplayType;
    this.userRoles = response.ProjectInfo.UserRoles || [];
    this.showEditIconForUser = this.pcs.clientSessionService.isAdmin
      || this.pcs.clientSessionService.isInRoleName('Company Administrator')
      || this.userRoles.some(role =>
        role.RoleName === 'Global Administrator'
        || role.RoleName === 'Company Administrator'
        || role.RoleName === 'Admin'
      );
    this.dataAsOf = new Date();
    if (this.moduleDisplaySetting.DisplayExecution && this.projectDisplayType.includes('scurve')) {
      this.loadExecutionChartData(this.projectId);
    }

    if (response.ProjectInfo.CXPlannerIdentifiers.length > 0) {
      this.pcs.apiService.executeCommandService.getCxPlanner(response.ProjectInfo.CXPlannerIdentifiers[0]).then((data) => {
        const stages = this.parseCxPlannerPayload(data);
        this.cxPlannerSummaryStage = this.transformData(stages);
        this.cxPlannerSummaryStage.projectId = response.ProjectInfo.CXPlannerIdentifiers[0];
        this.cxPlannerSummary = true;
      }).catch(() => {
        this.cxPlannerSummary = false;
      });
    }
  }

  toggleEditMenu(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.editMenuOpen = !this.editMenuOpen;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.editMenuOpen = false;
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent) {
    const tag = ((event.target as HTMLElement)?.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      return;
    }
    if (event.key === 'ArrowLeft') {
      this.moveLeft();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.moveRight();
      event.preventDefault();
    }
  }

  moveLeft() {
    if (this.canMoveLeft) {
      this.currentSlideIndex--;
    }
  }

  moveRight() {
    if (this.canMoveRight) {
      this.currentSlideIndex++;
    }
  }

  getTransform() {
    return `translateX(-${this.currentSlideIndex * 33.3333}%)`;
  }

  openProjectInProPak(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.editMenuOpen = false;
    const userName = this.pcs.clientSessionService.userName;
    const token = this.readinessSummary?.PassThroughAuthKey;
    const baseUrl = this.readinessSummary?.ProPakProjectUrl;
    if (baseUrl && userName && token) {
      const base = (baseUrl || '').split('#')[0];
      window.open(`${base}#${userName},${token},${this.projectId}`, '_blank');
      return;
    }
    this.pcs.notificationService.warning(
      'ProPak',
      'Unable to open this project in ProPak right now.',
      { timeOut: 3000 }
    );
  }

  /**
   * Hands the user off to the P6 Reporting dashboard, already signed in and scoped to this project.
   * The link carries a short-lived signed token minted server-side on every click, so nothing goes stale
   * while the summary page sits open. The blank tab is opened synchronously because browsers only allow
   * window.open() from inside the click handler itself - not after the await below.
   */
  async openDetailedReporting(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.editMenuOpen = false;
    if (this.openingDetailedReporting) {
      return;
    }
    this.openingDetailedReporting = true;
    const reportWindow = window.open('', '_blank');

    try {
      const response: GetP6ReportingLinkCommandResponse = await this.pcs.apiService.GetP6ReportingLink(
        { ProjectId: this.projectId } as GetP6ReportingLinkCommandRequest, true, false);

      if (!response || !response.Url) {
        if (reportWindow) { reportWindow.close(); }
        this.pcs.notificationService.warning('Detailed Reports is not available in this environment', '', { timeOut: 3000 });
        return;
      }

      if (reportWindow) {
        reportWindow.location.href = response.Url;
      } else {
        // Pop-up blocked before we ever got a handle - nothing left to do but tell the user.
        this.pcs.notificationService.warning('Please allow pop-ups for this site to open Detailed Reports', '', { timeOut: 5000 });
      }
    } catch (error) {
      // ExecuteCommandService already raised the notification for us - just don't leave a blank tab behind.
      if (reportWindow) { reportWindow.close(); }
      console.log('In ProjectSummaryComponent - openDetailedReporting(), request failed.', error);
    } finally {
      this.openingDetailedReporting = false;
    }
  }

  async generateAiInsights() {
    if (!this.projectId || this.insightsLoading) {
      return;
    }

    const requestProjectId = this.projectId;
    this.insightsLoading = true;
    try {
      const request: GetProjectAiInsightsCommandRequest = {
        ProjectId: requestProjectId,
        FactsJson: this.buildAiInsightsFactsJson()
      };
      const response: GetProjectAiInsightsCommandResponse =
        await this.pcs.apiService.GetProjectAiInsights(request, true, false);

      // Ignore late responses after the user switched projects.
      if (this.projectId !== requestProjectId) {
        return;
      }

      this.insightsText = (response && response.Insights) ? response.Insights.trim() : '';
      this.insightsGeneratedAt = response && response.GeneratedAtUtc
        ? new Date(response.GeneratedAtUtc)
        : new Date();
      this.insightsProjectId = requestProjectId;
      if (!this.insightsText) {
        this.insightsProjectId = null;
        this.pcs.notificationService.warning(
          'AI Insights',
          'No insights were returned. Please try again.',
          { timeOut: 4000 }
        );
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.log('In ProjectSummaryComponent - generateAiInsights() failed.', error);
    } finally {
      if (this.projectId === requestProjectId) {
        this.insightsLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  /** Clears on-demand AI Insights UI state (does not call the API). */
  private clearAiInsights(): void {
    this.insightsText = '';
    this.insightsGeneratedAt = null;
    this.insightsLoading = false;
    this.insightsProjectId = null;
  }

  /**
   * Builds a compact facts payload from data already loaded on this page.
   * Isolated helper — does not change how summary columns load their data.
   */
  private buildAiInsightsFactsJson(): string {
    const facts: any = {
      projectId: this.projectId,
      projectName: this.project?.ProPakProject?.Name || null,
      clientName: this.project?.ProPakProject?.Client?.Name
        || this.project?.ProPakProject?.Asset?.Client?.Name
        || null,
      assetName: this.project?.ProPakProject?.Asset?.Name || null
    };

    if (this.readinessSummary) {
      facts.readiness = {
        percentComplete: this.readinessSummary.PercentComplete,
        priorities: (this.readinessSummary.Priorities || []).map((p: any) => ({
          name: p.PriorityName,
          taskCountTotal: p.TaskCountTotal,
          taskCountComplete: p.TaskCountComplete,
          percentComplete: p.PercentComplete,
          categories: (p.Categories || []).slice(0, 12).map((c: any) => ({
            name: c.CategoryName,
            statusPercentage: c.StatusPercentage,
            satisfied: c.TaskCountSatisfied,
            inProgress: c.TaskCountInProgress,
            incomplete: c.TaskCountIncomplete,
            total: c.TaskCountTotal
          }))
        }))
      };
    }

    if (this.materialsSummary) {
      facts.materials = {
        percentComplete: this.materialsSummary.PercentComplete,
        totalItems: this.materialsSummary.TotalItems,
        schemas: (this.materialsSummary.MaterialsSchemas || []).slice(0, 12).map((s: any) => ({
          name: s.SchemaName,
          percentComplete: s.PercentComplete,
          totalItems: s.TotalItems,
          phases: (s.MaterialsPhases || []).slice(0, 8).map((ph: any) => ({
            name: ph.PhaseName,
            percentComplete: ph.PercentComplete,
            totalItems: ph.TotalItems
          }))
        }))
      };
    }

    if (this.executionSummary || this.executionLineChartData) {
      facts.execution = {
        percentComplete: this.executionLineChartData?.ActualPercentComplete
          ?? this.executionSummary?.PercentComplete
          ?? null,
        totalItems: this.executionSummary?.TotalItems ?? null,
        projectDataDate: this.executionSummary?.ProjectDataDate ?? null,
        baselineFinishDate: this.executionSummary?.BaselineFinishDate ?? null,
        projectFinishDate: this.executionSummary?.ProjectFinishDate ?? null,
        projectedStartDate: this.executionSummary?.ProjectedStartDate ?? null,
        wbsLevels: (this.executionSummary?.ExecutionWbsLevels || []).slice(0, 10).map((w: any) => ({
          name: w.StageName || w.Name,
          percentComplete: w.PercentComplete,
          stages: (w.Stages || []).slice(0, 8).map((st: any) => ({
            name: st.StageName || st.Name,
            percentComplete: st.PercentComplete
          }))
        }))
      };
    }

    if (this.cxPlannerSummary && Array.isArray(this.cxPlannerSummaryStage)) {
      facts.commissioning = {
        stages: this.cxPlannerSummaryStage.slice(0, 12).map((s: any) => ({
          name: s.StageName,
          percentComplete: s.PercentComplete,
          children: (s.Stages || []).slice(0, 8).map((c: any) => ({
            name: c.StageName,
            percentComplete: c.PercentComplete
          }))
        }))
      };
    }

    return JSON.stringify(facts);
  }

  loadFavoriteStatus() {
    const favoriteIds = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
    this.isFavorited = favoriteIds.includes(this.projectId);
  }

  toggleFavorite() {
    let favoriteIds = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
    if (favoriteIds.includes(this.projectId)) {
      favoriteIds = favoriteIds.filter((id: number) => id !== this.projectId);
      this.isFavorited = false;
    } else {
      favoriteIds.push(this.projectId);
      this.isFavorited = true;
    }
    localStorage.setItem('favoriteProjects', JSON.stringify(favoriteIds));
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { detail: { projectId: this.projectId } }));
    }
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    this.timerService.stopIdleMonitor();
    if (this.timerService.isTimerRunning()) {
      this.timerService.stopTimer();
      this.pcs.apiService.LogTimeOut({ ProjectId: this.projectId, IsAutoRouted: this.isAutoRouted, TimeOut: this.timerService.lastActivity, AccessArea: ProjectAccessArea.ProjectSummary } as any, false, false);
    }
  }
}
